import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    ComboBox,
    DefaultButton,
    Icon,
    IComboBox,
    IComboBoxOption,
    MessageBar,
    MessageBarType,
    PanelType,
    PrimaryButton,
    TextField
} from "office-ui-fabric-react";
import DirtyPanel from "../../../../common/DirtyPanel";
import { Dictionary } from "../../../../../entities/common";
import { findProjectTasks } from "../../../../task/taskFetchers";
import {
    TimesheetEditApplyResult,
    TimesheetEditMappingItem,
    TimesheetEditRow,
    TimesheetEditRowKind,
    TimesheetEditTarget,
    TimesheetEditValidateResult,
    TimesheetEditWarning
} from "../../../../../store/TimesheetApprovalStore";
import { CustomEntryBadge, FieldLabel, SourceProjectField, TargetProjectPicker, WarningIcon } from "./EditControls";
import {
    ProjectInfo,
    customOptionKey,
    groupWarningsByRow,
    projectWorkLabel,
    projectWorkOptionKey,
    resolveWarningText,
    validateDebounceMs
} from "./common";
import "./BulkEditApprovalRequestsPanel.css";

interface Props {
    requestIds: string[];
    sourceProject: ProjectInfo;
    sourceRows: TimesheetEditRow[];
    bulkEditValidate: (
        requestIds: string[],
        targetProjectId: string,
        mapping: TimesheetEditMappingItem[],
        callback?: (result: TimesheetEditValidateResult) => void
    ) => void;
    bulkEditApply: (
        requestIds: string[],
        targetProjectId: string,
        mapping: TimesheetEditMappingItem[],
        callback?: (result: TimesheetEditApplyResult) => void,
        onError?: () => void
    ) => void;
    onApplied: (result: TimesheetEditApplyResult) => void;
    onClose: () => void;
}

const BulkEditApprovalRequestsPanel = (props: Props) => {
    const { requestIds, sourceProject, sourceRows, bulkEditValidate, bulkEditApply, onApplied, onClose } = props;

    const [targetProject, setTargetProject] = useState<ProjectInfo | null>(null);
    const [taskOptions, setTaskOptions] = useState<IComboBoxOption[]>([]);
    const [mapping, setMapping] = useState<Dictionary<TimesheetEditTarget>>(() => buildInitialMapping(sourceRows));
    const [warnings, setWarnings] = useState<TimesheetEditWarning[]>([]);
    const [canApply, setCanApply] = useState(false);
    const [isApplying, setIsApplying] = useState(false);

    useEffect(() => {
        if (!targetProject) {
            setTaskOptions([]);
            return;
        }
        findProjectTasks(targetProject.id).then(tasks => setTaskOptions(tasks.map(_ => ({ key: _.id, text: _.name }))));
        setMapping(buildInitialMapping(sourceRows));
    }, [targetProject?.id]);

    const mappingItems = useMemo(
        (): TimesheetEditMappingItem[] => Object.keys(mapping).map(key => ({ sourceRowKey: key, target: mapping[key] })),
        [mapping]
    );

    useEffect(() => {
        if (!targetProject) {
            setWarnings([]);
            setCanApply(false);
            return undefined;
        }
        const handle = setTimeout(
            () =>
                bulkEditValidate(requestIds, targetProject.id, mappingItems, result => {
                    setWarnings(result.warnings);
                    setCanApply(result.canApply);
                }),
            validateDebounceMs
        );
        return () => clearTimeout(handle);
    }, [targetProject?.id, mappingItems, requestIds, bulkEditValidate]);

    const warningsByRow = useMemo(() => groupWarningsByRow(warnings), [warnings]);

    const targetOptions: IComboBoxOption[] = useMemo(
        () => [
            { key: customOptionKey, text: "Custom Time Entry" },
            { key: projectWorkOptionKey, text: projectWorkLabel },
            ...taskOptions
        ],
        [taskOptions]
    );

    const rowSelectedKey = (rowKey: string): string | undefined => {
        const target = mapping[rowKey];
        if (!target) {
            return undefined;
        }
        if (target.kind === TimesheetEditRowKind.Custom) {
            return customOptionKey;
        }
        if (target.kind === TimesheetEditRowKind.ProjectWork) {
            return projectWorkOptionKey;
        }
        return target.taskId ?? undefined;
    };

    const setRowTarget = (rowKey: string, optionKey: string) =>
        setMapping(prev => {
            const next = { ...prev };
            if (optionKey === customOptionKey) {
                next[rowKey] = { kind: TimesheetEditRowKind.Custom, customTimeEntryName: prev[rowKey]?.customTimeEntryName ?? "" };
            } else if (optionKey === projectWorkOptionKey) {
                next[rowKey] = { kind: TimesheetEditRowKind.ProjectWork };
            } else {
                next[rowKey] = { kind: TimesheetEditRowKind.Task, taskId: optionKey };
            }
            return next;
        });

    const setRowCustomName = (rowKey: string, name: string) =>
        setMapping(prev => ({
            ...prev,
            [rowKey]: { kind: TimesheetEditRowKind.Custom, customTimeEntryName: name }
        }));

    const apply = () => {
        if (!targetProject) {
            return;
        }
        setIsApplying(true);
        bulkEditApply(requestIds, targetProject.id, mappingItems, onApplied, () => setIsApplying(false));
    };

    return (
        <DirtyPanel
            className="bulk-edit-approval-requests-panel"
            type={PanelType.custom}
            customWidth="600px"
            isOpen
            isLightDismiss
            isDirty={!!targetProject}
            primaryCommandLabel="Apply"
            cancelCommandLabel="Cancel"
            onDismiss={onClose}
            onRenderHeader={() => (
                <div className="ms-Panel-header">
                    <p className="ms-Panel-headerText">Bulk Edit</p>
                    <div className="ms-Panel-secondaryText">
                        {requestIds.length} approval request{requestIds.length === 1 ? "" : "s"} selected
                    </div>
                </div>
            )}
            onRenderFooterContent={() => (
                <>
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                        Selected approval requests (both Approved and Pending Approval) will be recalled, removed, and
                        replaced with new Pending Approval requests for the target project. They will require re-approval
                        after this operation. The corresponding timesheets will be updated accordingly.
                    </MessageBar>
                    <div className="bulk-edit-footer">
                        <PrimaryButton text="Apply" disabled={!canApply || isApplying} onClick={apply} />
                        <DefaultButton text="Cancel" onClick={onClose} />
                    </div>
                </>
            )}>
            <div className="bulk-edit-panel">
                <div className="bulk-edit-description">
                    Choose where to move the time by selecting a target project and mapping the tasks.
                </div>

                <SourceProjectField
                    name={sourceProject.name}
                    tooltip="The project the selected approval requests currently report time to."
                />

                <div>
                    <FieldLabel
                        text="Target Project"
                        required
                        tooltip="The project the selected time will be moved to. Only projects with time tracking enabled are listed."
                    />
                    <TargetProjectPicker
                        selected={targetProject}
                        excludeProjectId={sourceProject.id}
                        onChange={setTargetProject}
                    />
                </div>

                {targetProject && (
                    <div className="bulk-edit-mapping">
                        <div className="bulk-edit-mapping-hint">
                            Map each source task to a task in the target project. Hours will be moved to the same days.
                        </div>
                        <div className="bulk-edit-mapping-header">
                            <div className="bulk-edit-col-source">Source ({sourceProject.name})</div>
                            <div className="bulk-edit-col-target">Target ({targetProject.name})</div>
                        </div>
                        {sourceRows.map(row => {
                            const label = row.label ?? projectWorkLabel;
                            const rowWarnings = warningsByRow[row.key] ?? [];
                            return (
                                <div className="bulk-edit-mapping-row" key={row.key}>
                                    <div className="bulk-edit-col-source">
                                        <div className="bulk-edit-source-field">
                                            <div className="bulk-edit-source-name">
                                                <span className="bulk-edit-row-label" title={label}>
                                                    {label}
                                                </span>
                                                {row.kind === TimesheetEditRowKind.Custom && <CustomEntryBadge />}
                                            </div>
                                            <span className="bulk-edit-hours">{row.hours}h</span>
                                        </div>
                                    </div>
                                    <Icon iconName="Forward" className="bulk-edit-arrow" />
                                    <div className="bulk-edit-col-target">
                                        <div className="bulk-edit-target-row">
                                            <TargetTaskSelect
                                                options={targetOptions}
                                                selectedKey={rowSelectedKey(row.key) ?? null}
                                                onSelect={key => setRowTarget(row.key, key)}
                                            />
                                            <WarningIcon messages={rowWarnings.map(resolveWarningText)} />
                                        </div>
                                        {mapping[row.key]?.kind === TimesheetEditRowKind.Custom && (
                                            <TextField
                                                placeholder="Custom time entry name"
                                                value={mapping[row.key]?.customTimeEntryName ?? ""}
                                                onChange={(_, newValue) => setRowCustomName(row.key, newValue ?? "")}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </DirtyPanel>
    );
};

const TargetTaskSelect = (props: {
    options: IComboBoxOption[];
    selectedKey: string | null;
    onSelect: (key: string) => void;
}) => {
    const comboRef = useRef<IComboBox>(null);
    return (
        <div
            className="timesheet-edit-target-select"
            onClick={e => (e.target as HTMLElement).tagName === "INPUT" && comboRef.current?.focus(true)}>
            <ComboBox
                componentRef={comboRef}
                className="input"
                allowFreeform
                autoComplete="on"
                useComboBoxAsMenuWidth
                placeholder="Select target"
                styles={{ root: { width: "100%" } }}
                options={props.options}
                selectedKey={props.selectedKey}
                onChange={(_, option) => option && props.onSelect(option.key as string)}
            />
        </div>
    );
};

const buildInitialMapping = (rows: TimesheetEditRow[]): Dictionary<TimesheetEditTarget> => {
    const result: Dictionary<TimesheetEditTarget> = {};
    for (const row of rows) {
        if (row.defaultTarget) {
            result[row.key] = { ...row.defaultTarget };
        }
    }
    return result;
};

export default BulkEditApprovalRequestsPanel;
